import { defineComponent, createApp, type PropType, type VNode, ref } from 'vue-demi'
import { type VueDirectRenderer } from '../vue-direct-renderer'
import { normalizeCSSUnitObject } from '../../../../../../utils/dom-style'
import { logger } from '../../../../../../utils/logger'
function padFloat (n: number, padLimit = 6) {
  const integerPartLength = Math.floor(n).toString().length

  if (integerPartLength < padLimit) {
    return n.toFixed(5).substring(0, padLimit)
  } else {
    return n.toFixed(0)
  }
}

const buttonEl = defineComponent({
  props: {
    label: String
  },
  render (h) {
    return h('div', {
      style: {
        padding: '1px',
        cursor: 'pointer',
        pointerEvents: 'all',
        display: 'inline-block',
        background: 'rgba(0,0,0,0.70)'
      },
      on: this.$listeners
    }, ['[ ', ...this.$slots.default, ' ]'])
  }
})

const textEl = defineComponent({
  props: {
    label: String
  },
  render (h) {
    return h('div', {
      style: {
        padding: '1px',
        display: 'inline-block',
        background: 'rgba(0,0,0,0.70)'
      },
      on: this.$listeners
    }, [...this.$slots.default])
  }
})

const debugComponent = defineComponent({
  props: {
    renderer: {
      type: Object as PropType<VueDirectRenderer>
    }
  },
  render (h, context) {
    if (this.show === false) return h()
    const renderer: VueDirectRenderer = this.renderer
    const children: VNode[] = []
    children.push(
      h(buttonEl, {
        on: {
          click: () => { this.$emit('close') }
        }
      }, 'x'),
      h(textEl, `embedTarget: ${renderer.ctx?.embedTarget?.name as string ?? 'unknown'}`),
      h(textEl, `embedStrategy: ${renderer.ctx?.settings.strategy as string ?? 'unknown'}`),
      h(textEl, `layoutMode: ${renderer.currentMode.value as string ?? 'unknown'}`),
      h(textEl, `window: w=${renderer.windowSizeRef.width.value}, h=${renderer.windowSizeRef.height.value}`),
      h(textEl, `mediaEl: w=${padFloat(renderer.videoSizeRef.width.value)}, h=${padFloat(renderer.videoSizeRef.height.value)}, x=${padFloat(renderer.videoSizeRef.left.value)}, y=${padFloat(renderer.videoSizeRef.top.value)}`),
      h(textEl, `detailsTarget: ${renderer.currentLayout.value?.details?.target?.mode ?? 'undefined'}`),
      h(buttonEl, {
        on: {
          click: () => { logger.log('currentLayout', renderer.currentLayout.value) }
        }
      }, 'Print currentLayout'),
      h(buttonEl, {
        on: {
          click: () => { logger.log('settings', renderer.ctx.settings) }
        }
      }, 'Print settings'),
      h(buttonEl, {
        on: {
          click: () => { logger.log('targetElement', renderer.ctx.targetEl) }
        }
      }, 'Print targetElement'),
      h(buttonEl, {
        on: {
          click: () => { renderer.printPartition() }
        }
      }, 'Print partition')
    )
    return h('div', children.map(i => h('div', [i])))
  }
})

export function mount (renderer: VueDirectRenderer) {
  const el = document.createElement('div')
  const open = ref(true)
  const app = createApp({
    name: 'EmbedderDebug',
    render: h => {
      if (!open.value) return h('div')
      return h(debugComponent, {
        props: {
          renderer
        },
        on: {
          close: () => {
            open.value = false
          }
        },
        style: {
          fontSize: '80%',
          fontFamily: 'monospace',
          position: 'fixed',
          ...normalizeCSSUnitObject({
            top: renderer.videoSizeRef.top.value,
            left: renderer.videoSizeRef.left.value,
            width: renderer.videoSizeRef.width.value,
            height: renderer.videoSizeRef.height.value
          }),
          opacity: 0.5,
          boxSizing: 'border-box',
          color: 'white',
          padding: '0.5em',
          pointerEvents: 'none'
        }
      })
    }
  })
  document.body.appendChild(el)
  app.mount(el)
  return {
    setOpen (value: boolean) {
      open.value = value
    },
    destroy () {
      app.unmount()
    }
  }
}
